import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content:
    "Hi, I'm RIRI, Rhea's warm little help assistant. Ask me anything about her mentoring, the plans, how the donation works, booking, or the Career Discovery Assessment. What would you like to know?",
};

const QUICK_LINKS = [
  { label: "Services & pricing", section: "pricing" },
  { label: "The framework", section: "framework" },
];

const getSessionId = () => {
  let id = localStorage.getItem("rhea_chat_session");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("rhea_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  useEffect(() => {
    if (sessionStorage.getItem("rhea_chat_teaser_seen")) return;
    const t = setTimeout(() => setTeaser(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const dismissTeaser = () => {
    setTeaser(false);
    sessionStorage.setItem("rhea_chat_teaser_seen", "1");
  };

  const openChat = () => {
    setOpen(true);
    dismissTeaser();
  };

  const goToSection = (section) => {
    setOpen(false);
    const scroll = () => {
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 500);
    } else {
      scroll();
    }
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId.current, message });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry, I could not respond just now. Please try again in a moment." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm h-[32rem] max-h-[70vh] bg-white border border-[#041C3E] shadow-2xl flex flex-col"
          >
            <div className="bg-[#041C3E] text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-[#D94833]" />
                <div>
                  <p className="font-head font-bold leading-tight" data-testid="chat-assistant-name">RIRI</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/60">Rhea's help assistant</p>
                </div>
              </div>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F9FAFB]">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#D94833] text-white"
                        : "bg-white border border-[#E5E7EB] text-[#041C3E]"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div data-testid="chat-typing" className="flex justify-start">
                  <div className="bg-white border border-[#E5E7EB] px-4 py-3 text-sm text-[#9CA3AF]">Typing…</div>
                </div>
              )}
            </div>

            <div className="border-t border-[#E5E7EB] bg-white">
              <div className="flex gap-2 px-3 pt-3 overflow-x-auto">
                {QUICK_LINKS.map((q) => (
                  <button
                    key={q.section}
                    data-testid={`chat-quicklink-${q.section}`}
                    onClick={() => goToSection(q.section)}
                    className="shrink-0 text-xs font-semibold border border-[#E5E7EB] px-3 py-1.5 hover:border-[#041C3E] hover:bg-[#F3F4F6] transition-colors"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-center gap-2 p-3"
              >
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your question…"
                  className="flex-1 border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-3 py-2.5 text-sm transition-colors"
                />
                <button
                  data-testid="chat-send-button"
                  type="submit"
                  disabled={busy || !input.trim()}
                  className="bg-[#041C3E] text-white p-2.5 hover:bg-[#D94833] transition-colors disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {teaser && !open && (
          <motion.div
            data-testid="chat-teaser"
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-50 w-[15rem] bg-white border border-[#041C3E] shadow-xl p-4"
          >
            <button
              data-testid="chat-teaser-close"
              onClick={dismissTeaser}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#041C3E] text-white flex items-center justify-center hover:bg-[#D94833] transition-colors"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
            <button data-testid="chat-teaser-open" onClick={openChat} className="text-left">
              <p className="font-head font-bold text-sm">Hi, I'm RIRI. Questions about working with Rhea?</p>
              <p className="text-xs text-[#3A4A63] mt-1">Ask me about the plans, the donation model, or booking. I'm here to help.</p>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-toggle-button"
        onClick={() => (open ? setOpen(false) : openChat())}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#D94833] text-white shadow-xl flex items-center justify-center hover:bg-[#C03F2A] transition-colors"
        aria-label="Open RIRI chat assistant"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>
    </>
  );
};
